import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ColorTheme, TextTheme } from "../../../components/globaStyledComponents/globalStyledComponentsStyling";
import RoomPopup from "../../../components/roomPopup/roomPopup";
import { amenitiesSrc } from "../../../utils/amenityIcons";
import { getUserCurrencyLabel, getUserCurrencySymbol } from "../../../utils/userCurrencyUtils";
import { UserModel } from "../../../interfaces/models/userModel";
import {
    ViewLisitingAvailableRoomWrapper,
    ViewLisitingAvailableRoomTitle,
} from "../viewListingStyling";

interface HotelRoom {
    id: string;
    name: string;
    capacity: number;
    bathroomType: number;
    genderRestriction: number;
    roomType: number;
    viewType: number;
    balconies: number;
    quality: number;
    bedding: number;
    bedrooms: number;
    amenities: string[];
    bathrooms: number;
    roomImages: string[];
    floorType: number;
    clubRoom: boolean;
    familyRoom: boolean;
}

interface HotelRate {
    rateId: string;
    name: string;
    bedType: string | null;
    amenities: string[];
    paymentAmount: number;
    guestBreakdown: {
        priceItems: Array<{
            label: string;
            amount: number;
            amountFormatted: string;
        }>;
        priceDiffItems: any[];
        subtotalItems: Array<{
            label: string;
            amount: number;
            amountFormatted: string;
        }>;
    };
    meal: {
        type: string;
        hasBreakfast: boolean;
        noChildMeal: boolean;
    };
    cancellation: {
        freeCancellationBefore: string;
        policies: Array<{
            startAt: string | null;
            endAt: string | null;
            amount: number;
            currency: string;
        }>;
    };
    roomDetails: RoomDetails;
}

interface RoomDetails {
    capacity: number;
    bathroomType: number;
    genderRestriction: number;
    roomType: number;
    viewType: number;
    balconies: number;
    quality: number;
    bedding: number;
    bedrooms: number;
    floorType: number;
    clubRoom: boolean;
    familyRoom: boolean;
}

const RoomCardsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 50px;
`;

const RoomCard = styled.div`
    display: flex;
    gap: 24px;
    padding: 20px;
    border-radius: 24px;
    background-color: #fff;
    position: relative;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    }
`;

const ImageContainer = styled.div`
    flex-shrink: 0;
`;

const RoomImage = styled.img`
    width: 200px;
    height: 200px;
    object-fit: cover;
    border-radius: 16px;
    cursor: pointer;

    @media (max-width: 767px) {
        width: 96px;
        height: 96px;
    }
`;

const PlaceholderImage = styled.div`
    width: 200px;
    height: 200px;
    background-color: #f5f5f5;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 767px) {
        width: 96px;
        height: 96px;
    }
`;

const PlaceholderText = styled.span`
    color: #999;
    ${TextTheme.sans_300}
`;

const ContentContainer = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-width: 0;
`;

const ContentSection = styled.div`
    margin-top: 0;
    min-width: 0;
`;

const RoomTitle = styled.h3`
    margin-bottom: 8px;
    margin-top: 0;
    color: ${ColorTheme.Black.color};
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
    white-space: normal;
    text-overflow: unset;
    overflow: visible;
    ${TextTheme.sans_700_semi}

    @media (max-width: 767px) {
        ${TextTheme.sans_400_semi}
    }
`;

const RoomDescription = styled.div`
    margin-top: 8px;
    ${TextTheme.sans_300}
    color: ${ColorTheme.Grey.color};
    margin-bottom: 12px;
`;

const AmenitiesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
`;

const AmenityTag = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background-color: #f8f9fa;
    border-radius: 12px;
    ${TextTheme.sans_200}
    color: ${ColorTheme.Grey2.color};
`;

const AmenityIcon = styled.img`
    width: 14px;
    height: 14px;
    object-fit: contain;
`;

const AmenitySvgIcon = styled.svg`
    width: 14px;
    height: 14px;
    display: inline-block;
`;

const MoreAmenitiesTag = styled.div`
    display: flex;
    align-items: center;
    padding: 4px 8px;
    background-color: #f8f9fa;
    border-radius: 12px;
    ${TextTheme.sans_200}
    color: ${ColorTheme.Black.color};
`;

const PriceContainer = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    margin-top: auto;
`;

const PriceText = styled.span`
    color: ${ColorTheme.Black.color};
    ${TextTheme.sans_700_semi}
`;

const TotalText = styled.span`
    color: ${ColorTheme.Black.color};
    ${TextTheme.sans_300}
`;

const ReserveButton = styled.button`
    position: absolute;
    bottom: 16px;
    right: 16px;
    background-color: ${ColorTheme.Black.color};
    color: white;
    border: none;
    border-radius: 24px;
    padding: 0 24px;
    height: 48px;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 100px;
    ${TextTheme.sans_300_semi}

    &:hover {
        background-color: #333;
        transform: translateY(-1px);
    }

    @media (max-width: 767px) {
        display: none;
    }
`;

const RateContainer = styled.button<{ selected?: boolean }>`
    margin-bottom: 8px;
    padding: 8px;
    border: 1px solid ${props => (props.selected ? ColorTheme.Black.color : '#e0e0e0')};
    border-radius: 12px;
    background-color: ${props => (props.selected ? ColorTheme.Grey5.color : '#f9f9f9')};
    text-align: left;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;

    &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        transform: translateY(-2px);
    }
`;

const RateHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
`;

const RateName = styled.div`
    font-weight: 600;
    color: #333;
`;

const RatePrice = styled.div`
	${TextTheme.sans_400_semi}
	color: #000000;
    margin-left: 16px;
`;

const RateAttributesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
`;

// Function to match hotel rooms with quote room details
const matchRoomsWithQuote = (hotelRooms: HotelRoom[], roomDetails?: RoomDetails[]): HotelRoom[] => {
    if (!roomDetails || roomDetails.length === 0) {
        return hotelRooms;
    }

    const matchedRooms = hotelRooms.filter(room => {
        // Check if room matches ANY of the roomDetails from the quote
        const isMatch = roomDetails.some(roomDetail => {
            const matches = room.capacity === roomDetail.capacity &&
                   room.bathroomType === roomDetail.bathroomType &&
                   room.genderRestriction === roomDetail.genderRestriction &&
                   room.roomType === roomDetail.roomType &&
                   room.viewType === roomDetail.viewType &&
                   room.balconies === roomDetail.balconies &&
                   room.quality === roomDetail.quality &&
                   room.bedding === roomDetail.bedding &&
                   room.bedrooms === roomDetail.bedrooms &&
                   room.floorType === roomDetail.floorType &&
                   room.clubRoom === roomDetail.clubRoom &&
                   room.familyRoom === roomDetail.familyRoom;
            
            
            return matches;
        });
        
        
        return isMatch;
    });

    return matchedRooms;
};

export default function ViewListingHotelRooms({ 
    hotelRooms,
    hotelRates,
    roomDetails,
    onRoomImageClick,
    onReserveRoom,
    listing,
    dates,
    guests,
    userModel
}: { 
    hotelRooms: HotelRoom[];
    hotelRates: HotelRate[];
    roomDetails?: RoomDetails[];
    onRoomImageClick?: (imageIndex: number, roomImages: string[]) => void;
    onReserveRoom?: (room: HotelRoom) => void;
    listing: any;
    dates: any;
    guests: any;
    userModel: UserModel | null;
}) {
    const [showAllRooms, setShowAllRooms] = useState(false);
    const [selectedRateIdByRoomName, setSelectedRateIdByRoomName] = useState<Record<string, string | null>>({});
    const [popupRoom, setPopupRoom] = useState<any | null>(null);
    const navigate = useNavigate();
    const currencyLabel = getUserCurrencyLabel(userModel);
    const currencySymbol = getUserCurrencySymbol(userModel);
    
    
    if (!hotelRooms || hotelRooms.length === 0) return null;
    
    // Match rooms with quote room details
    const matchedRooms = matchRoomsWithQuote(hotelRooms, roomDetails);

    // Deduplicate rooms by name to prevent duplicate cards
    const uniqueMatchedRooms = matchedRooms.filter((room, index, self) => 
        index === self.findIndex(r => r.name === room.name)
    );


    // Find all matching rates for each room and filter out rooms with no rates
    const roomsWithRates = uniqueMatchedRooms.map(room => {
        const matchingRates = hotelRates?.filter(rate => {
            if (!rate.name || !room.name) return false;
            
            // Exclude rates that include "Not Guaranteed" as an amenity
            if (rate.amenities?.some(amenity => 
                amenity.toLowerCase().includes('not-guaranteed')
            )) {
                return false;
            }
            
            // More robust matching - check if rate name is contained in room name or vice versa
            const roomNameLower = room.name.toLowerCase().trim();
            const rateNameLower = rate.name.toLowerCase().trim();
            
            return roomNameLower.includes(rateNameLower) || 
                   rateNameLower.includes(roomNameLower) ||
                   roomNameLower === rateNameLower;
        }) || [];
        
        
        // Filter out duplicate rates and keep only the cheaper one
        const uniqueRates = matchingRates.reduce((acc: HotelRate[], currentRate) => {
            // Check if there's already a rate with the same name
            const existingRate = acc.find(rate => rate.name === currentRate.name);
            
            if (!existingRate) {
                // No existing rate with this name, add it
                acc.push(currentRate);
            } else if (currentRate.paymentAmount < existingRate.paymentAmount) {
                // Current rate is cheaper, replace the existing one
                const index = acc.findIndex(rate => rate.name === currentRate.name);
                acc[index] = currentRate;
            }
            // If current rate is more expensive, ignore it
            
            return acc;
        }, []);

        return {
            ...room,
            rates: uniqueRates,
            primaryRate: uniqueRates[0] // Keep the first rate as primary for backward compatibility
        };
    }).filter(room => room.rates.length > 0) // Only show rooms that have matching rates
    .sort((a, b) => {
        // Sort by primary rate price (cheapest first)
        const priceA = a.primaryRate?.paymentAmount || 0;
        const priceB = b.primaryRate?.paymentAmount || 0;
        return priceA - priceB;
    });


    // Hide the entire section if there are no rooms with rates
    if (roomsWithRates.length === 0) {
        return null;
    }

    // Display logic for pagination
    const displayedRooms = showAllRooms ? roomsWithRates : roomsWithRates.slice(0, 8);

    const getSelectedRateId = (roomName: string, rates: HotelRate[]): string | null => {
        const current = selectedRateIdByRoomName[roomName] ?? null;
        if (current && rates.some(r => r.rateId === current)) return current;
        return rates[0]?.rateId ?? null;
    };

    const getSelectedRate = (room: any): HotelRate | null => {
        const selectedId = getSelectedRateId(room.name, room.rates);
        return room.rates.find((r: HotelRate) => r.rateId === selectedId) || room.rates[0] || null;
    };

    const mapRoomToPopupData = (room: any) => {
        const selectedRate = getSelectedRate(room);
        const bedTypeFromRate = selectedRate?.bedType || '';
        const fallbackBed = getBedTypeDescription(room.bedding);
        return {
            id: room.id,
            name: room.name,
            images: (room.roomImages || []).map((img: string) => img),
            room_amenities: room.amenities || [],
            maxGuests: room.capacity,
            bedTypes: [bedTypeFromRate || fallbackBed].filter(Boolean),
            price: selectedRate ? selectedRate.paymentAmount / 100 : undefined,
            rg_ext: {
                balcony: room.balconies,
                bathroom: room.bathrooms,
                bedrooms: room.bedrooms,
                capacity: room.capacity,
                class: room.quality,
                club: room.clubRoom ? 1 : 0,
                family: room.familyRoom ? 1 : 0,
                floor: room.floorType,
                sex: room.genderRestriction,
                view: room.viewType,
            },
        };
    };
    const hasMoreRooms = roomsWithRates.length > 8;


    return (
        <ViewLisitingAvailableRoomWrapper hasAmenities={false}>
            <ViewLisitingAvailableRoomTitle>Choose your room</ViewLisitingAvailableRoomTitle>
            <RoomCardsContainer>
                {roomsWithRates.map((room, index) => (
                    <RoomCard key={room.id || `room-${index}`} onClick={() => setPopupRoom(room)}>
                        <ImageContainer>
                            {room.roomImages && room.roomImages.length > 0 ? (
                                <RoomImage 
                                    onClick={(e) => { e.stopPropagation(); onRoomImageClick?.(0, room.roomImages); }}
                                    src={room.roomImages[0]} 
                                    alt={room.name} 
                                />
                            ) : (
                                <PlaceholderImage>
                                    <PlaceholderText>No image</PlaceholderText>
                                </PlaceholderImage>
                            )}
                        </ImageContainer>
                        
                        <ContentContainer>
                            <ContentSection>
                                <RoomTitle>
                                    {[
                                        room.name,
                                        getViewType(room.viewType)
                                    ].filter(Boolean).join(" ")}
                                </RoomTitle>
                                
                                <RoomDescription>
                                    {[
                                        room.capacity > 0 && `${room.capacity} guests`,
                                        room.bedrooms > 0 && `${room.bedrooms} bedroom${room.bedrooms > 1 ? 's' : ''}`,
                                        room.bathrooms > 0 && `${room.bathrooms} bath${room.bathrooms > 1 ? 's' : ''}`,
                                        room.balconies > 0 && `${room.balconies} balcony${room.balconies > 1 ? 's' : ''}`
                                    ].filter(Boolean).join(' • ')}
                                </RoomDescription>

                                {/* Room amenities */}
                                {room.amenities.length > 0 && (
                                    <AmenitiesContainer>
                                        {room.amenities.slice(0, 8).map((amenity, index) => (
                                            <AmenityTag key={index}>
                                                <AmenityIcon 
                                                    src={amenitiesSrc[amenity] || amenitiesSrc.default}
                                                    alt={amenity}
                                                />
                                                <span>{formatAmenityName(amenity)}</span>
                                            </AmenityTag>
                                        ))}
                                        {room.amenities.length > 8 && (
                                            <MoreAmenitiesTag>
                                                +{room.amenities.length - 8} more
                                            </MoreAmenitiesTag>
                                        )}
                                    </AmenitiesContainer>
                                )}

                                {/* Rate attributes (breakfast, cancellation, etc.) */}
                                {room.rates && room.rates.length > 0 && (
                                    <AmenitiesContainer>
                                        {room.rates.map((rate, rateIndex) => (
                                            <RateContainer 
                                                key={rateIndex}
                                                selected={getSelectedRateId(room.name, room.rates) === rate.rateId}
                                                onClick={(e) => { e.stopPropagation(); setSelectedRateIdByRoomName(prev => ({ ...prev, [room.name]: rate.rateId })); }}
                                            >
                                                <RateHeader>
                                                    <RateName>
                                                        {rate.name}
                                                    </RateName>
                                                    <RatePrice>
                                                        {rate.guestBreakdown?.subtotalItems?.[0]?.amountFormatted || `$${(rate.paymentAmount / 100).toFixed(2)}`}
                                                    </RatePrice>
                                                </RateHeader>
                                                <RateAttributesContainer>
                                                    {/* Breakfast */}
                                                    {rate.meal?.hasBreakfast && (
                                                        <AmenityTag>
                                                            <AmenitySvgIcon
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-label="breakfast"
                                                            >
                                                                <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" />
                                                                <path d="M7 2v20" />
                                                                <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" />
                                                            </AmenitySvgIcon>
                                                            <span>Breakfast included</span>
                                                        </AmenityTag>
                                                    )}
                                                    
                                                    {/* Free cancellation */}
                                                    {rate.cancellation?.freeCancellationBefore && (
                                                        <AmenityTag>
                                                            <AmenityIcon 
                                                                src="https://d292awxalydr86.cloudfront.net/Listing+page+icons/Cancellation.svg"
                                                                alt="free cancellation"
                                                            />
                                                            <span>Free cancellation</span>
                                                        </AmenityTag>
                                                    )}
                                                    
                                                    {/* Non-smoking */}
                                                    {rate.amenities?.includes('non-smoking') && (
                                                        <AmenityTag>
                                                            <AmenitySvgIcon
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-label="non-smoking"
                                                            >
                                                                <path d="M12 12H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h13"/>
                                                                <path d="M18 8c0-2.5-2-2.5-2-5"/>
                                                                <path d="m2 2 20 20"/>
                                                                <path d="M21 12a1 1 0 0 1 1 1v2a1 1 0 0 1-.5.866"/>
                                                                <path d="M22 8c0-2.5-2-2.5-2-5"/>
                                                                <path d="M7 12v4"/>
                                                            </AmenitySvgIcon>
                                                            <span>Non-smoking</span>
                                                        </AmenityTag>
                                                    )}

                                                    {/* Smoking */}
                                                    {rate.amenities?.includes('smoking') && (
                                                        <AmenityTag>
                                                            <AmenitySvgIcon
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-label="smoking"
                                                            >
                                                                <path d="M17 12H3a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h14"/>
                                                                <path d="M18 8c0-2.5-2-2.5-2-5"/>
                                                                <path d="M21 16a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/>
                                                                <path d="M22 8c0-2.5-2-2.5-2-5"/>
                                                                <path d="M7 12v4"/>
                                                            </AmenitySvgIcon>
                                                            <span>Smoking</span>
                                                        </AmenityTag>
                                                    )}
                                                    
                                                    {/* Bed type */}
                                                    {rate.bedType && (
                                                        <AmenityTag>
                                                            <AmenitySvgIcon
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                aria-label="bed"
                                                            >
                                                                <path d="M2 4v16"/>
                                                                <path d="M2 8h18a2 2 0 0 1 2 2v10"/>
                                                                <path d="M2 17h20"/>
                                                                <path d="M6 8v9"/>
                                                            </AmenitySvgIcon>
                                                            <span>{rate.bedType}</span>
                                                        </AmenityTag>
                                                    )}
                                                    
                                                    {/* Meal type */}
                                                    {rate.meal?.type && rate.meal.type !== 'breakfast' && (
                                                        <AmenityTag>
                                                            <AmenityIcon 
                                                                src="https://d292awxalydr86.cloudfront.net/Listing+page+icons/Meal.svg"
                                                                alt="meal type"
                                                            />
                                                            <span>{rate.meal.type.charAt(0).toUpperCase() + rate.meal.type.slice(1)} included</span>
                                                        </AmenityTag>
                                                    )}
                                                    
                                                    {/* Rate-specific amenities */}
                                                    {rate.amenities?.filter(amenity => amenity !== 'non-smoking' && amenity !== 'smoking').map((amenity, amenityIndex) => (
                                                        <AmenityTag key={`${rateIndex}-${amenityIndex}`}>
                                                            {amenity.toLowerCase().includes('bed') ? (
                                                                <AmenitySvgIcon
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="14"
                                                                    height="14"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                    stroke="currentColor"
                                                                    strokeWidth="2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    aria-label="bed"
                                                                >
                                                                    <path d="M2 4v16"/>
                                                                    <path d="M2 8h18a2 2 0 0 1 2 2v10"/>
                                                                    <path d="M2 17h20"/>
                                                                    <path d="M6 8v9"/>
                                                                </AmenitySvgIcon>
                                                            ) : (
                                                                <AmenityIcon 
                                                                    src={amenitiesSrc[amenity] || amenitiesSrc.default}
                                                                    alt={amenity}
                                                                />
                                                            )}
                                                            <span>{formatAmenityName(amenity)}</span>
                                                        </AmenityTag>
                                                    ))}
                                                </RateAttributesContainer>
                                            </RateContainer>
                                        ))}
                                    </AmenitiesContainer>
                                )}
                            </ContentSection>

                            <PriceContainer>
                                {(() => {
                                    console.log(`💰 Price display for "${room.name}":`, {
                                        hasRates: !!room.rates,
                                        ratesLength: room.rates?.length || 0,
                                        rates: room.rates?.map(r => ({ name: r.name, amount: r.paymentAmount })) || []
                                    });
                                    return room.rates && room.rates.length > 0;
                                })() ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                            <PriceText>
                                                {currencySymbol}{(room.rates[0].paymentAmount / 100).toFixed(2)} {currencyLabel}
                                            </PriceText>
                                            <TotalText>
                                                total
                                            </TotalText>
                                        </div>
                                    </div>
                                ) : (
                                    <PriceText>
                                        Add dates for price
                                    </PriceText>
                                )}
                            </PriceContainer>
                        </ContentContainer>

                        <ReserveButton onClick={(e) => {
                            e.stopPropagation();
                            if (dates.startDate && dates.startDate !== "anyArrival" && dates.endDate && dates.endDate !== "anyDeparture") {
                                const arrivalDate = `${String((dates.startDate as Date).getDate()).padStart(2, "0")}-${String((dates.startDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.startDate as Date).getFullYear()}`;
                                const departureDate = `${String((dates.endDate as Date).getDate()).padStart(2, "0")}-${String((dates.endDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.endDate as Date).getFullYear()}`;
                                
                                const adultsString = guests.adults ? `adults=${guests.adults}` : "";
                                const childrenString = guests.children ? `children=${guests.children}` : "";
                                const petsString = guests.pets ? `pets=${guests.pets}` : "";
                                const infantsString = guests.infants ? `infants=${guests.infants}` : "";
                                const selectedRateId = getSelectedRateId(room.name, room.rates);
                                const rateIdString = selectedRateId ? `rateId=${selectedRateId}` : "";
                                const searchParamsStrings = [adultsString, childrenString, petsString, infantsString, rateIdString].filter((param) => param !== "").join("&");
                                
                                navigate(`/book/${listing.id}/${arrivalDate}/${departureDate}${searchParamsStrings ? `?${searchParamsStrings}` : ""}`);
                            } else {
                                onReserveRoom?.(room);
                            }
                        }}>
                            Reserve
                        </ReserveButton>
                    </RoomCard>
                ))}

                {hasMoreRooms && !showAllRooms && (
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                        <button
                            onClick={() => setShowAllRooms(true)}
                            style={{
                                backgroundColor: 'transparent',
                                color: ColorTheme.Black.color,
                                border: `1px solid ${ColorTheme.Grey1.color}`,
                                borderRadius: '24px',
                                padding: '0 24px',
                                height: '48px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                minWidth: '100px',
                                ...TextTheme.sans_300_semi
                            }}
                        >
                            Show more rooms
                        </button>
                    </div>
                )}
            </RoomCardsContainer>
            {popupRoom && (
                <RoomPopup 
                    isOpen={!!popupRoom}
                    onClose={() => setPopupRoom(null)}
                    roomData={mapRoomToPopupData(popupRoom)}
                    onReserve={() => {
                        if (dates.startDate && dates.startDate !== "anyArrival" && dates.endDate && dates.endDate !== "anyDeparture") {
                            const arrivalDate = `${String((dates.startDate as Date).getDate()).padStart(2, "0")}-${String((dates.startDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.startDate as Date).getFullYear()}`;
                            const departureDate = `${String((dates.endDate as Date).getDate()).padStart(2, "0")}-${String((dates.endDate as Date).getMonth() + 1).padStart(2, "0")}-${(dates.endDate as Date).getFullYear()}`;
                            const adultsString = guests.adults ? `adults=${guests.adults}` : "";
                            const childrenString = guests.children ? `children=${guests.children}` : "";
                            const petsString = guests.pets ? `pets=${guests.pets}` : "";
                            const infantsString = guests.infants ? `infants=${guests.infants}` : "";
                            const selectedRate = getSelectedRate(popupRoom);
                            const rateIdString = selectedRate?.rateId ? `rateId=${selectedRate.rateId}` : "";
                            const searchParamsStrings = [adultsString, childrenString, petsString, infantsString, rateIdString].filter((param) => param !== "").join("&");
                            navigate(`/book/${listing.id}/${arrivalDate}/${departureDate}${searchParamsStrings ? `?${searchParamsStrings}` : ""}`);
                        } else {
                            onReserveRoom?.(popupRoom);
                        }
                    }}
                />
            )}
        </ViewLisitingAvailableRoomWrapper>
    );
}

// Helper function to get bed type description
const getBedTypeDescription = (bedding: number): string => {
    switch (bedding) {
        case 3:
            return "Full double bed";
        case 4:
            return "Twin beds";
        case 7:
            return "Triple beds";
        default:
            return "";
    }
}

// Helper function to get room quality description
const getRoomQuality = (quality: number): string => {
    switch (quality) {
        case 2:
            return "Standard";
        case 5:
            return "Superior";
        case 6:
            return "Deluxe";
        default:
            return "";
    }
}

// Helper function to get view type description
const getViewType = (viewType: number): string => {
    switch (viewType) {
        case 5:
            return "with city view";
        default:
            return "";
    }
}

// Helper function to format amenity names
const formatAmenityName = (amenity: string) => {
    return amenity
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};